import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import type { AxiosError } from 'axios';
import useTranslation from 'next-translate/useTranslation';

import { useWalletBalance } from '~/domains/payments/hooks/useWalletBalance';
import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import useProtectedGuard from '~/domains/protectionGuards/hooks/useProtectedGuard';
import { ProtectionGuardType } from '~/domains/protectionGuards/types';
import {
  makeBuyback,
  getBulkContestTeamPicks,
} from '~/domains/contest/domains/services/contest.service.api';
import type { BuybackRequest } from '~/domains/contest/domains/services/interfaces/buyback.interface';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import { reportEvent } from '~/domains/analytics';

const REDIRECT_TIMEOUT = 5_000;

type ReviveEntryModalProps = {
  contestId: string;
  entryId: string;
  entryName: string;
  contest: FullContest;
  onClose: () => void;
};

function useReviveEntryModal({
  contestId,
  entryId,
  entryName,
  contest,
  onClose,
}: ReviveEntryModalProps) {
  const { t } = useTranslation('contest');
  const router = useRouter();
  const [currentLocation, setCurrentLocation] = useState(router.pathname);

  const {
    data: balance,
    isLoading: isBalanceLoading,
    refetch: refetchBalance,
  } = useWalletBalance();
  const { openModal, openModalUnique, closeModal, enableClose } = useContext(RootModalsContext);
  const queryClient = useQueryClient();

  // eslint-disable-next-line local-rules/require-query-stale-time
  const { data: teamPicksData } = useQuery({
    queryKey: ['teamPicks', entryId],
    queryFn: () => getBulkContestTeamPicks({ entryIds: [entryId] }),
    enabled: !!entryId,
  });

  const actualRevivalsUsed = useMemo(() => {
    const entryData = teamPicksData?.[entryId];
    if (!entryData?.slates) return 0;
    return Object.values(entryData.slates).filter((slate) => slate.hasBuyback).length;
  }, [teamPicksData, entryId]);

  const maxRevivalsAllowed = contest.settings?.buybacks?.maxBuybacksPerEntry || 0;
  const revivalsRemaining = Math.max(0, maxRevivalsAllowed - actualRevivalsUsed);

  const { isAccessible } = useProtectedGuard({
    action: 'revive an entry',
    protectionLevels: [
      ProtectionGuardType.KYC,
      ProtectionGuardType.LOCATION,
      ProtectionGuardType.GEOCOMPLY,
      ProtectionGuardType.CONTEST,
    ],
    checkOnMount: true,
    contestId,
  });

  const reviveMutation = useMutation({
    mutationFn: async (payload: BuybackRequest & { isEntertainmentOnly?: boolean }) => {
      if (contestId && !payload.isEntertainmentOnly) {
        if (isAccessible) openModalUnique({ type: 'DepositModal' });
      }

      return makeBuyback({ entryId: payload.entryId });
    },
    onSuccess: async (data) => {
      enableClose();

      queryClient.invalidateQueries({ queryKey: ['teamPicks'] });
      queryClient.invalidateQueries({ queryKey: ['standings'] });

      const { data: updatedBalance } = await refetchBalance();

      closeModal();
      openModal({
        type: 'ReviveEntrySuccessModal',
        reviveResult: data,
        contestId,
        balanceInDollars: updatedBalance?.availableBalance || 0,
        revivalsRemaining: revivalsRemaining - 1,
      });

      reportEvent('Contest > Revive Entry', {
        contest_id: contestId,
        entry_id: entryId,
        entry_fee: contest.entry_fee,
        entry_fee_usd: contest.entry_fee / 100,
        af_revenue: contest.entry_fee / 100,
      });
    },
    onError: (error: AxiosError<{ error?: string; message?: string }>) => {
      enableClose();

      let errorMessage = t('common.reviveEntryModal.errors.GENERIC');

      if (error.response?.status === 409) {
        const errorType = error.response.data?.error;
        switch (errorType) {
          case 'BUYBACK_NOT_ENABLED':
            errorMessage = t('common.reviveEntryModal.errors.BUYBACK_NOT_ENABLED');
            break;
          case 'BUYBACK_DEADLINE_EXPIRED':
            errorMessage = t('common.reviveEntryModal.errors.BUYBACK_DEADLINE_EXPIRED');
            break;
          case 'ENTRY_NOT_ELIMINATED':
            errorMessage = t('common.reviveEntryModal.errors.ENTRY_NOT_ELIMINATED');
            break;
          case 'BUYBACK_ALREADY_PURCHASED':
            errorMessage = t('common.reviveEntryModal.errors.BUYBACK_ALREADY_PURCHASED');
            break;
          default:
            errorMessage = error.response.data?.message || errorMessage;
        }
      } else if (error.response?.status === 402) {
        errorMessage = t('common.reviveEntryModal.errors.INSUFFICIENT_FUNDS');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      showNotification({
        title: t('common.reviveEntryModal.errors.title'),
        message: errorMessage,
        color: 'red',
        autoClose: 5000,
      });

      onClose();
    },
  });

  const isLoading = isBalanceLoading;

  const handleRevive = useCallback(async () => {
    const isEntertainmentOnly = contest.settings?.entertainmentOnlySettings?.enabled;

    reviveMutation.mutate({
      entryId,
      isEntertainmentOnly,
    });
  }, [contest.settings?.entertainmentOnlySettings?.enabled, entryId, reviveMutation]);

  const handleDepositClick = useCallback(() => {
    openModal({
      type: 'DepositModal',
      contestIdForPostDeposit: contestId,
    });
  }, [contestId, openModal]);

  useEffect(() => {
    if (!currentLocation) {
      setCurrentLocation(router.pathname);
    } else if (currentLocation !== router.pathname) {
      onClose();
    }
  }, [currentLocation, onClose, router.pathname]);

  const state = useMemo(() => {
    if (isLoading) {
      return 'loading';
    }
    if (reviveMutation.isLoading) {
      return 'submitting';
    }
    return 'default';
  }, [isLoading, reviveMutation.isLoading]);

  return {
    state,
    handleRevive,
    handleDepositClick,
    entryName,
    balanceInDollars: balance?.availableBalance || 0,
    contest,
    revivalsAvailable: revivalsRemaining,
    isCreateEntriesReady: !isLoading && !reviveMutation.isLoading,
    isBalanceLoading: isLoading,
    reviveErrorMessage: reviveMutation.error?.message,
  };
}

export default useReviveEntryModal;

export { REDIRECT_TIMEOUT };
